import { useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import type { Lang } from '../../types/cms';
import type { AdminCommon } from '../../pages/Admin';

const LANGS: { code: Lang; label: string }[] = [
  { code: 'tc', label: '繁體中文' },
  { code: 'sc', label: '简体中文' },
  { code: 'en', label: 'English' },
];

/** Leaf paths of an i18n tree (arrays flattened by index). */
function flattenKeys(obj: any, prefix = ''): string[] {
  return Object.keys(obj).reduce((acc: string[], k) => {
    const pre = prefix ? `${prefix}.` : '';
    const val = obj[k];
    if (val && typeof val === 'object') {
      if (Array.isArray(val)) {
        val.forEach((item, i) => {
          if (item && typeof item === 'object') acc.push(...flattenKeys(item, `${pre}${k}.${i}`));
          else acc.push(`${pre}${k}.${i}`);
        });
      } else {
        acc.push(...flattenKeys(val, pre + k));
      }
    } else {
      acc.push(pre + k);
    }
    return acc;
  }, []);
}

function getAt(obj: any, path: string): string {
  let val = obj;
  for (const part of path.split('.')) {
    if (val === undefined || val === null) return '';
    val = Array.isArray(val) && !isNaN(Number(part)) ? val[Number(part)] : val[part];
  }
  return typeof val === 'string' ? val : '';
}

/** Build a minimal nested object assigning a single path (for deep-merge save). */
function setAt(target: any, path: string, value: string) {
  const parts = path.split('.');
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = Array.isArray(cur) ? Number(parts[i]) : parts[i];
    if (cur[p] === undefined || typeof cur[p] !== 'object') cur[p] = {};
    cur = cur[p];
  }
  const last = Array.isArray(cur) ? Number(parts[parts.length - 1]) : parts[parts.length - 1];
  cur[last] = value;
  return target;
}

export default function ContentEditor({ draft, onSave, notify }: AdminCommon) {
  const [edits, setEdits] = useState<Record<string, Partial<Record<Lang, string>>>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [section, setSection] = useState<string>('all');

  // Canonical key list: flatten the tc tree (all trees share the same shape).
  const keys = useMemo(
    () =>
      flattenKeys(draft.i18n?.tc || {}).filter(
        (k) => !k.startsWith('team.members') // consultants are managed in their own tab
      ),
    [draft]
  );

  const sections = useMemo(
    () => Array.from(new Set(keys.map((k) => k.split('.')[0]))),
    [keys]
  );

  const valueOf = (key: string, lang: Lang) => edits[key]?.[lang] ?? getAt(draft.i18n?.[lang], key);

  const setValue = (key: string, lang: Lang, value: string) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [lang]: value } }));

  const saveRow = async (key: string) => {
    setSavingKey(key);
    try {
      const payload: Record<Lang, any> = { en: {}, tc: {}, sc: {} };
      for (const { code } of LANGS) {
        setAt(payload[code], key, valueOf(key, code));
      }
      await onSave({ i18n: payload });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (e) {
      console.error(e);
      notify('儲存失敗，請重試。', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  const visible = section === 'all' ? keys : keys.filter((k) => k.startsWith(section + '.'));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-5">
      <h2 className="text-lg font-bold text-navy-900 mb-1">網站內容編輯器</h2>
      <p className="text-sm text-gray-500 mb-4">
        每列同時編輯三個語言；儲存後寫入草稿。按「Ctrl/Cmd + F」可用瀏覽器搜尋內容。
      </p>

      {/* Section filter */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          onClick={() => setSection('all')}
          className={`px-3 py-1 rounded-full text-sm transition-colors ${
            section === 'all' ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          全部
        </button>
        {sections.map((s) => (
          <button
            key={s}
            onClick={() => setSection(s)}
            className={`px-3 py-1 rounded-full text-sm transition-colors ${
              section === s ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {visible.map((key) => {
          const dirty = edits[key] && Object.values(edits[key]).some((v) => v !== undefined);
          return (
            <div key={key} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <p className="font-mono text-xs font-bold text-navy-900 bg-gray-100 px-2 py-1 rounded">{key}</p>
                {dirty && (
                  <button
                    onClick={() => saveRow(key)}
                    disabled={savingKey === key}
                    className="flex items-center gap-1 bg-gold-500 text-navy-900 text-sm font-semibold px-3 py-1.5 rounded hover:bg-gold-600 transition-colors disabled:opacity-50"
                  >
                    <Save className="h-4 w-4" /> {savingKey === key ? '儲存中…' : '儲存此列'}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {LANGS.map(({ code, label }) => {
                  const val = valueOf(key, code);
                  const long = val.length > 80;
                  return (
                    <div key={code}>
                      <label className="block text-xs text-gray-400 mb-1">{label}</label>
                      {long ? (
                        <textarea
                          rows={4}
                          className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-2 focus:ring-navy-100 outline-none"
                          value={val}
                          onChange={(e) => setValue(key, code, e.target.value)}
                        />
                      ) : (
                        <input
                          className="w-full border border-gray-200 rounded p-2 text-sm focus:ring-2 focus:ring-navy-100 outline-none"
                          value={val}
                          onChange={(e) => setValue(key, code, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && <p className="text-center text-gray-400 py-8">沒有內容鍵值。</p>}
      </div>
    </div>
  );
}
